import type { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import type { ITextSnippetDTO } from '@modeso/types__aumico-lib-annualreport-be';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import Debug from 'debug';
import { of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import * as fromActions from '../actions/text-snippet.action';
import { AumicoTextSnippetService } from '../services/text-snippet.service';

const debug = Debug('aumico:aumico-lib-annualreport-fe:TextSnippetEffects');

@Injectable()
export class TextSnippetEffects {
    constructor(private readonly actions$: Actions, private readonly service: AumicoTextSnippetService) {}

    createTextSnippet$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.CreateTextSnippet),
            mergeMap((action) => {
                return this.service.createTextSnippet$(action.payload).pipe(
                    map((response: ITextSnippetDTO) => {
                        debug(response);
                        return fromActions.OnCreateTextSnippetSuccessfully({
                            payload: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.OnCreateTextSnippetFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    getAllTextSnippets$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.GetAllTextSnippets.type),
            mergeMap(() => {
                return this.service.getAllTextSnippets().pipe(
                    map((response: ITextSnippetDTO[]) => {
                        debug(response);
                        return fromActions.OnGetAllTextSnippetsSuccessfully({
                            payload: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.OnGetAllTextSnippetsFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    deleteTextSnippets$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.DeleteTextSnippets),
            mergeMap((payload) => {
                return this.service.deleteTextSnippets$(payload.textSnippetsIds).pipe(
                    map(() => {
                        return fromActions.OnDeleteTextSnippetsSuccessfully({
                            textSnippetsIds: payload.textSnippetsIds,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.OnDeleteTextSnippetsFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    updateTextSnippet$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.UpdateTextSnippet),
            mergeMap((action) => {
                return this.service.updateTextSnippet$(action.payload).pipe(
                    map((response: ITextSnippetDTO) => {
                        return fromActions.OnUpdateTextSnippetSuccessfully({
                            payload: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.OnUpdateTextSnippetFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    getTextSnippetById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.GetTextSnippetById),
            mergeMap((action) => {
                return this.service.getTextSnippetById$(action.payload).pipe(
                    map((response) => {
                        return fromActions.OnGettingTextSnippetByIdSuccessfully({ payload: response });
                    }),
                    catchError((error) => {
                        return of(fromActions.OnGettingTextSnippetByIdFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );
}
